import React from "react";
import { Drawer } from "@mui/material";

const DEFAULT_WIDTH = 440;
const TRANSITION = "width 0.25s ease, opacity 0.2s ease";

/**
 * Reusable collapsible side-drawer shell.
 * Renders `children` inside a permanent MUI Drawer with a smooth open/close transition.
 * Toggle is handled externally (e.g. a button in the navbar).
 *
 * @param {boolean}  open       Whether the drawer is visible.
 * @param {number}   width      Drawer width in px (default 440).
 * @param {string}   anchor     "left" or "right" (default "left").
 * @param {boolean}  overlay    When true, the drawer is fixed to the viewport
 *                              (absolute left of the screen, below the navbar)
 *                              instead of participating in the parent flex layout.
 * @param {number}   topOffset  Navbar height in px used when `overlay` is true (default 50).
 * @param {React.Node} children Content rendered inside the drawer body.
 */
export default function TreeViewDrawer({
  open,
  width = DEFAULT_WIDTH,
  anchor = "left",
  overlay = false,
  topOffset = 50,
  children,
}) {
  return (
    <Drawer
      variant="permanent"
      anchor={anchor}
      sx={
        overlay
          ? {
              position: "fixed",
              left: 0,
              top: topOffset,
              bottom: 0,
              height: `calc(100vh - ${topOffset}px)`,
              width: open ? width : 0,
              flexShrink: 0,
              zIndex: (theme) => theme.zIndex.drawer + 2,
              display: { xs: "none", sm: "block" },
              transition: TRANSITION,
              overflow: "hidden",
              pointerEvents: open ? "auto" : "none",
              "& .MuiDrawer-paper": {
                position: "relative",
                width,
                height: "100%",
                maxHeight: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                transition: TRANSITION,
                opacity: open ? 1 : 0,
              },
            }
          : {
              width: open ? width : 0,
              flexShrink: 0,
              display: { xs: "none", sm: "block" },
              transition: TRANSITION,
              overflow: "hidden",
              "& .MuiDrawer-paper": {
                position: "relative",
                width,
                boxSizing: "border-box",
                transition: TRANSITION,
                opacity: open ? 1 : 0,
              },
            }
      }
    >
      {children}
    </Drawer>
  );
}
